import { IS_SERVER } from 'src/utils/checkRenderEnv';
import {
  checkIsSliderFullWidth,
  getContainerTracks,
  getGridGapsForAxes,
  getOneCellGeometry,
  INITIAL_SCROLL_PERCENTAGE,
  isLogoSliderAutoScroll,
} from '../../utils';
import { COMMON_LAYOUT_TYPE } from '../../components/Testimonials/Constants';
import { useAutoScrollOrRotation } from '../../hooks/useAutoScrollOrRotation';
import { useProductCardElements } from '../../../ProductListWrapper/utils';
import { useSetViewAllBtnConfig } from '../../hooks/useSetViewAllBtnConfig';
import { useLayoutTypes } from '../../hooks/useLayoutTypes';
import { useState } from 'react';
import { ADMIN_WIDGETS } from 'src/constants/widgets';
import { LAYOUT_TYPE } from '../../constant';

export const useGetLayoutBasedUtilities = ({
  listPresentationConfig,
  cardFrameConfig,
  isMobile,
  contentTypeConditions,
  props,
  globalStyle,
  sectionContainerRef,
  itemContainerRef,
  itemCountTotal,
  moreItemsAvailable,
  instaUserData
}) => {
  const { isLayout4 } = useLayoutTypes({ listPresentationConfig });
  const isProductTestimonials = listPresentationConfig?.type === ADMIN_WIDGETS.PRODUCT_TESTIMONIALS.type
  const [carouselIndicatorConfigs, setCarouselIndicatorConfigs] = useState({
    scrollPercentage: INITIAL_SCROLL_PERCENTAGE,
    overflowSectionCount: 1,
    currentFocusedSectionItem: 1, // Starts from 1
    currentFocusedSectionExact: 1, // This is not ceiled, floored, or rounded (unlike currentFocusedSectionItem)
    direction: 'to-right',
  });

  const { aspectRatio: productCardAspectRatio } = useProductCardElements();

  const { isAutoRotateEnabled, autoRotateTimer } = useAutoScrollOrRotation({
    listPresentationConfig,
  });
  const carouselConfigs = globalStyle?.carousel?.config;

  const isLayoutCarousel =
    listPresentationConfig?.layoutType === 'carousel' && !cardFrameConfig?.autoScroll;

  const aspectRatio = cardFrameConfig?.aspectRatio || '1:1';
  const sectionColors =
    listPresentationConfig?.colors?.colorSchemes?.[
      listPresentationConfig?.colors?.selectedColorSchemeIndex
    ];
  const isMoveableHeading = contentTypeConditions?.instagram && listPresentationConfig?.layoutName === LAYOUT_TYPE.layout3
  const instaHandleProps = contentTypeConditions.instagram ?
    { showInstaHandle: listPresentationConfig?.showInstaHandle, instaHandle: instaUserData?.user?.username || '', instaHandlePosition: listPresentationConfig?.instaHandlePosition} : {}


  const featuredReelConfigs = {
    expandFocusedReel:
      isMobile &&
      contentTypeConditions.featuredReels &&
      cardFrameConfig?.mobileEnlargeAndFocus,
  };

  // Returns number of columns & rows
  const containerTracks = getContainerTracks({
    isLayoutCarousel,
    itemCount: itemCountTotal,
    isMobile: isMobile,
    listPresentationConfig,
    expandSelectedReel: featuredReelConfigs.expandFocusedReel,
    isTestimonials: contentTypeConditions.testimonials,
    // useStaticNumColumns:
    // contentTypeConditions.testimonials || contentTypeConditions?.multiColumnForImage,
  });

  // Item gaps
  const gridGapAxis = getGridGapsForAxes({
    isMobile: isMobile,
    cardFrameConfig,
    listPresentationConfig,
    type: listPresentationConfig?.type,
    isLayoutTypeCard: listPresentationConfig?.appearance === 'card',
    expandSelectedReel: featuredReelConfigs.expandFocusedReel,
    numColumns: containerTracks.numColumnsOriginal,
  });

  // Paddings & margin calculations
  // 1
  function getMobilePadding() {
    if(contentTypeConditions.instagram || isProductTestimonials) {
      return 20;
    }
    return 16;
  }
  const containerHorizontalPadding = !isMobile ? 40 : !isLayoutCarousel ? getMobilePadding() : 0;
  const contentHorizontalPadding = !containerHorizontalPadding
    ? !isMobile
      ? 40
      : contentTypeConditions.testimonials
        ? 10
        : 20
    : 0;
  // 2
  let listHorizontalPadding =
    isLayoutCarousel &&
    isMobile &&
    !(
      contentTypeConditions.gallery &&
      listPresentationConfig?.layoutName !== COMMON_LAYOUT_TYPE.TWO
    )
      ? contentTypeConditions.testimonials
        ? 10
        : 20
      : 0;
  let listVerticalPadding = 0;

  const isCardScaleAnimation =
    (cardFrameConfig?.hoverType === 'zoomInCard' && !isMobile) ||
    (cardFrameConfig?.mobileEnlargeAndFocus && isMobile);
  if (isCardScaleAnimation) {
    listVerticalPadding += 20;
    listHorizontalPadding += 20;
  }

  // Section Width calculations
  const SECTION_WIDTH = !IS_SERVER
    ? sectionContainerRef?.clientWidth -
      (props?.isBlogListingPage ? 0 : containerHorizontalPadding * 2)
    : isMobile
      ? 480
      : 720;

  const showCarouselIndicators = (function () {
    const areUsableLayouts =
      !contentTypeConditions.testimonials ||
      (contentTypeConditions.testimonials &&
        listPresentationConfig?.layoutName !== COMMON_LAYOUT_TYPE.FOUR);

    const isWidthCorrect = itemContainerRef?.clientWidth < itemContainerRef?.scrollWidth;
    return isLayoutCarousel && areUsableLayouts && isWidthCorrect;
  })();

  const showArrow = isLayoutCarousel
    ? isMobile
      ? carouselConfigs?.showArrowInMobile && showCarouselIndicators
      : showCarouselIndicators
    : false;

  const slideOffsetMultiplier = {
    forWidth:
      isMobile || (contentTypeConditions.testimonials && containerTracks.numColumns === 1)
        ? 1
        : 2,
    forGap:
      isMobile || (contentTypeConditions.testimonials && containerTracks.numColumns === 1)
        ? 1
        : 2,
  };

  // Remaining width from section after removing gaps & x-padding
  const cardContainerWidthForEachCell =
    SECTION_WIDTH +
    gridGapAxis.columnGap -
    containerTracks.numColumns * gridGapAxis.columnGap -
    2 * listHorizontalPadding;

  // For carousel: Remove some width from columns to partially show the next one i.e.
  // reduce width slightly for each card so that an overflowed card is shown slightly to nudge the user to scroll
  const widthAdditionals = !contentTypeConditions.testimonials
    ? isLayoutCarousel && itemCountTotal > containerTracks.numColumns && isMobile
      ? -containerTracks.numColumns * 6
      : 0
    : 0;

  // Width for each column (product item)
  const oneGridCellGeometry = getOneCellGeometry({
    cardContainerWidthForEachCell,
    containerTracks,
    aspectRatio:
      !contentTypeConditions.featured || contentTypeConditions.featuredReels
        ? aspectRatio
        : productCardAspectRatio,
    widthAdditionals,
    type: listPresentationConfig?.type || '',
    isMobile: isMobile,
    isLayoutTypeCard: listPresentationConfig?.appearance === 'card',
  });

  const slideOffset =
    oneGridCellGeometry.width * slideOffsetMultiplier.forWidth +
    gridGapAxis.columnGap * slideOffsetMultiplier.forGap;

  const columns = isMobile
    ? listPresentationConfig?.numColumnsMobile
    : listPresentationConfig?.numColumnsDesktop;

  const showAutoRotation =
    isLayoutCarousel &&
    isAutoRotateEnabled &&
    autoRotateTimer &&
    itemCountTotal > columns;

  const columnWidthShift = oneGridCellGeometry.width + gridGapAxis.columnGap;

  const shouldAutoScrollSlider = isLogoSliderAutoScroll({
    cardFrameConfig,
    listPresentationConfig,
  });

  const isSliderFullWidth = checkIsSliderFullWidth({
    cardFrameConfig,
    listPresentationConfig,
  });

  const numItemsToShow = containerTracks.numColumns * containerTracks.numRows;

  const { viewAllItemsConfig, setViewAllItemsConfig } = useSetViewAllBtnConfig({
    contentTypeConditions,
    isLayoutCarousel,
    listPresentationConfig,
    moreItemsAvailable,
    itemCountTotal,
    numItemsToShow,
    isBlogListingPage: props?.isBlogListingPage,
    isTestimonialListingPage: props?.isTestimonialListingPage,
  });

  const getConfigsAndUtils = () => {
    const showAuthorImageList =
      (contentTypeConditions.testimonials || contentTypeConditions.productTestimonial) && isLayout4 && !!itemCountTotal;

    return {
      generalValues: {
        numItemsToShow,
        isSliderFullWidth,
        oneGridCellGeometry,
        cardContainerWidthForEachCell,
        isCardScaleAnimation,
        sectionColors,
        viewAllItemsConfig,
        showAuthorImageList,
        shouldAutoScrollSlider,
        columnWidthShift,
        showAutoRotation,
        columns,
        slideOffset,
        widthAdditionals,
        slideOffsetMultiplier,
        showArrow,
        showCarouselIndicators,
        contentHorizontalPadding,
        gridGapAxis,
        containerTracks,
        autoRotateTimer,
        carouselConfigs,
        isLayout4,
        featuredReelConfigs,
        isTestimonialListingPage: props?.isTestimonialListingPage,
        carouselIndicatorConfigs,
        isLayoutCarousel,
        listVerticalPadding,
        listHorizontalPadding,
        containerHorizontalPadding,
        isMoveableHeading,
        instaHandleProps
      },
      setters: { setViewAllItemsConfig, setCarouselIndicatorConfigs },
    };
  };

  return {
    getConfigsAndUtils,
  };
};
